import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import TextTransitionEffect from '../components/TextTransitionEffect';
import TechBackground from '../components/TechBackground';
import ProjectDisplay from '../components/ProjectDisplay';
import TeamCarousel from '../components/TeamCarousel';
import TestimonialSection from '../components/TestimonialSection';
import BlogPostsSection from '../components/BlogPostsSection';
import HomeServiceCard from '../components/HomeServiceCard';
import '../styles/HomeServiceCard.css';

const HomePage = () => {
  const [servicesRef, servicesInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const [projectsRef, projectsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const heroTexts = [
    "Crafting Digital Excellence",
    "Where Innovation Meets Intelligence",
    "Powering Tomorrow's Technology",
    "Transforming Ideas into Impact",
    "Building Smart Solutions Together"
  ];

  // Enhanced service data with features for the new card design
  const homeServices = [
    {
      icon: '🔮',
      title: 'Web Development',
      description: 'Custom, responsive websites that convert visitors into customers',
      iconClass: 'service-icon-web',
      features: [
        'Responsive Design',
        'SEO Optimization',
        'Performance Tuning',
        'Custom CMS Integration'
      ]
    },
    {
      icon: '🤖',
      title: 'AI Integration',
      description: 'Leverage the power of AI to automate and enhance your business',
      iconClass: 'service-icon-ai',
      features: [
        'Machine Learning Models',
        'Natural Language Processing',
        'Predictive Analytics',
        'Custom AI Solutions'
      ]
    },
    {
      icon: '✨',
      title: 'Branding & Design',
      description: 'Create a memorable brand identity that resonates with your audience',
      iconClass: 'service-icon-design',
      features: [
        'Logo Design',
        'Brand Identity',
        'Style Guidelines',
        'Marketing Collateral'
      ]
    },
    {
      icon: '💫',
      title: 'Digital Advertising',
      description: 'Strategic digital campaigns that drive growth and engagement',
      iconClass: 'service-icon-ads',
      features: [
        'PPC Campaign Management',
        'Social Media Advertising',
        'Retargeting Strategies',
        'Performance Analytics'
      ]
    },
    {
      icon: '⚡',
      title: 'IT Development & Maintenance',
      description: 'Comprehensive IT solutions with ongoing support and maintenance',
      iconClass: 'service-icon-it',
      features: [
        'Legacy System Migration',
        'Software Maintenance',
        'Security Updates',
        'Performance Monitoring'
      ]
    },
    {
      icon: '🌟',
      title: 'SaaS Development',
      description: 'Custom SaaS solutions that scale with your business needs',
      iconClass: 'service-icon-saas',
      features: [
        'Multi-tenant Architecture',
        'Subscription Management',
        'API Development',
        'Cloud Deployment'
      ]
    }
  ];

  return (
    <motion.div 
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.section 
        className="hero"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="hero-content">
          <div className="hero-text-wrapper">
            <TextTransitionEffect texts={heroTexts} interval={4000} />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Leveraging AI and cutting-edge technology to create innovative solutions
          </motion.p>
          <motion.button 
            className="cta-button"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore Our Solutions
          </motion.button>
        </div>
      </motion.section>

      <motion.section 
        className="values-transition"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <motion.div 
          className="values-content"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            visible: {
              opacity: 1,
              transition: {
                when: "beforeChildren",
                staggerChildren: 0.2
              }
            },
            hidden: { opacity: 0 }
          }}
        >
          <motion.p 
            className="values-intro"
            variants={{
              visible: { opacity: 1, y: 0 },
              hidden: { opacity: 0, y: 30 }
            }}
          >
            <span className="highlight-text">Empowering Digital Excellence</span> At Oasis Logic Technologies, 
            we don't just provide services; we architect transformative solutions. Our expertise spans across 
            cutting-edge technologies, enabling businesses to thrive in the digital age. Through innovative strategies 
            and advanced implementations, we help organizations unlock their full potential and stay ahead in today's 
            competitive landscape.
          </motion.p>

          <div className="values-grid">
            {[
              {
                title: "Innovation",
                description: "We prioritize creativity in everything we do, constantly seeking new ways to deliver outstanding solutions.",
                icon: "💡",
                delay: 0.2,
                iconClass: "icon-innovation"
              },
              {
                title: "Customer-Centricity",
                description: "We focus on our clients' needs and collaborate closely to develop personalized solutions.",
                icon: "🤝",
                delay: 0.4,
                iconClass: "icon-customer"
              },
              {
                title: "Integrity",
                description: "We maintain the highest standards of professionalism and transparency in all our dealings.",
                icon: "⚖️",
                delay: 0.6,
                iconClass: "icon-integrity"
              },
              {
                title: "Excellence",
                description: "We are committed to providing high-quality services and solutions that meet or exceed expectations.",
                icon: "✨",
                delay: 0.8,
                iconClass: "icon-excellence"
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                className="value-card glass-morphism"
                variants={{
                  visible: { opacity: 1, y: 0 },
                  hidden: { opacity: 0, y: 50 }
                }}
                transition={{
                  duration: 0.5,
                  delay: value.delay
                }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.span 
                  className={`value-icon ${value.iconClass}`}
                  animate={{ 
                    rotateY: [0, 180, 360],
                    scale: [1, 1.1 + Math.random() * 0.1, 1],
                    filter: [
                      'drop-shadow(0 0 0 rgba(56, 189, 248, 0))',
                      `drop-shadow(0 ${15 + Math.random() * 10}px ${20 + Math.random() * 10}px rgba(56, 189, 248, 0.6))`,
                      'drop-shadow(0 0 0 rgba(56, 189, 248, 0))'
                    ]
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    repeatType: "loop",
                    ease: "easeInOut",
                    times: [0, 0.5, 1]
                  }}
                  style={{ 
                    display: "inline-block",
                    willChange: "transform"
                  }}
                >
                  {value.icon}
                </motion.span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.section>

      <motion.section 
        className="about-section glass-morphism"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <motion.div 
          className="about-content"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.2
              }
            },
            hidden: { opacity: 0 }
          }}
        >
          <motion.h2 
            className="section-title gradient-text"
            variants={{
              visible: { opacity: 1, y: 0 },
              hidden: { opacity: 0, y: 20 }
            }}
          >
            Who We Are
          </motion.h2>
          <motion.p 
            className="about-intro"
            variants={{
              visible: { opacity: 1, y: 0 },
              hidden: { opacity: 0, y: 30 }
            }}
          >
            Oasis Logic Technologies LLC is a leading tech company committed to providing cutting-edge IT solutions 
            to help businesses thrive in today's competitive market.
          </motion.p>

          <div className="about-cards-grid">
            <motion.div
              className="company-card glass-morphism"
              variants={{
                visible: { opacity: 1, x: 0 },
                hidden: { opacity: 0, x: -50 }
              }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <h3><span className="accent-text">Our Vision</span></h3>
              <p>Our vision is to become a global leader in delivering innovative technology solutions that enable 
                businesses to achieve digital transformation and sustain long-term success.</p>
            </motion.div>

            <motion.div
              className="company-card glass-morphism"
              variants={{
                visible: { opacity: 1, x: 0 },
                hidden: { opacity: 0, x: 50 }
              }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <h3><span className="accent-text">Our Mission</span></h3>
              <p>Our mission is to empower businesses with innovative, efficient, and scalable technology solutions 
                that drive success and growth.</p>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      <motion.section 
        ref={servicesRef}
        className="services-section"
        initial={{ opacity: 0, y: 50 }}
        animate={servicesInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <TechBackground />
        <motion.h2 
          className="gradient-text"
          initial={{ opacity: 0, y: -20 }}
          animate={servicesInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: "2.5rem" }} // Add more space below the heading
        >
          Our Services
        </motion.h2>
        <div className="services-grid">
          {homeServices.map((service, index) => (
            <HomeServiceCard 
              key={service.title} 
              service={service} 
              index={index} 
            />
          ))}
        </div>
      </motion.section>

      <motion.section
        ref={projectsRef}
        className="projects-section"
        initial={{ opacity: 0, y: 50 }}
        animate={projectsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2>Featured Projects</h2>
        
        <motion.div 
          className="featured-project"
          initial={{ opacity: 0, y: 30 }}
          animate={projectsInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="featured-project-container">
            <div className="featured-project-demo">
              <div className="project-interface">
                <div className="interface-header">
                  <div className="header-controls">
                    <div className="control-dot"></div>
                    <div className="control-dot"></div>
                    <div className="control-dot"></div>
                  </div>
                  <div className="interface-address">
                    <div className="address-bar">
                      <span className="address-icon">🔒</span>
                      app.analytics-platform.com/dashboard
                    </div>
                  </div>
                </div>
                <div className="project-demo-content">
                  <div className="tech-grid-bg"></div>
                  <div className="tech-glow top-right"></div>
                  <div className="tech-glow bottom-left"></div>
                  <div className="tech-connectors">
                    {[...Array(5)].map((_, i) => (
                      <div 
                        key={i} 
                        className="connector-line" 
                        style={{
                          width: `${100 + Math.random() * 100}px`,
                          top: `${20 + i * 40 + Math.random() * 20}%`,
                          left: `${Math.random() * 30}%`,
                          transform: `rotate(${-30 + Math.random() * 60}deg)`
                        }}
                      ></div>
                    ))}
                  </div>
                  <div className="demo-widgets">
                    <div className="demo-widget chart-widget">
                      <div className="widget-header">
                        <div className="widget-title">
                          <span className="widget-title-icon">📊</span>
                          Performance Analytics
                        </div>
                        <div className="widget-controls">
                          <div className="widget-control"></div>
                          <div className="widget-control"></div>
                        </div>
                      </div>
                      <div className="widget-body">
                        <div className="chart-lines">
                          {[...Array(5)].map((_, i) => (
                            <div key={i} className="chart-line"></div>
                          ))}
                        </div>
                        <div className="chart-bars">
                          {[25, 40, 60, 45, 35, 55, 70, 60].map((height, i) => (
                            <div 
                              key={i} 
                              className="chart-bar" 
                              style={{ 
                                '--bar-height': `${height}%`,
                                '--bar-index': i
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="demo-widget data-widget">
                      <div className="widget-header">
                        <div className="widget-title">
                          <span className="widget-title-icon">📈</span>
                          Key Metrics
                        </div>
                        <div className="widget-controls">
                          <div className="widget-control"></div>
                          <div className="widget-control"></div>
                        </div>
                      </div>
                      <div className="widget-body">
                        <div className="data-item">
                          <div className="data-label">
                            <div className="data-indicator"></div>
                            <div className="data-text">Active Users</div>
                          </div>
                          <div className="data-value">14,392</div>
                        </div>
                        <div className="data-item">
                          <div className="data-label">
                            <div className="data-indicator purple"></div>
                            <div className="data-text">Data Processed</div>
                          </div>
                          <div className="data-value">1.8 TB</div>
                        </div>
                        <div className="data-item">
                          <div className="data-label">
                            <div className="data-indicator green"></div>
                            <div className="data-text">Accuracy Rate</div>
                          </div>
                          <div className="data-value">99.4%</div>
                        </div>
                        <div className="data-item">
                          <div className="data-label">
                            <div className="data-indicator orange"></div>
                            <div className="data-text">Response Time</div>
                          </div>
                          <div className="data-value">0.2s</div>
                        </div>
                      </div>
                    </div>
                    <div className="demo-widget terminal-widget" style={{ gridColumn: "span 2" }}>
                      <div className="widget-header">
                        <div className="widget-title">
                          <span className="widget-title-icon">💻</span>
                          Terminal
                        </div>
                        <div className="widget-controls">
                          <div className="widget-control"></div>
                          <div className="widget-control"></div>
                        </div>
                      </div>
                      <div className="widget-body">
                        <div className="terminal-lines">
                          <div className="terminal-line">
                            <span className="terminal-prompt">$</span>
                            <span className="terminal-command">npm run analyze-data</span>
                          </div>
                          <div className="terminal-line">
                            <span className="terminal-output">Processing data streams...</span>
                          </div>
                          <div className="terminal-line">
                            <span className="terminal-output">Running ML prediction models...</span>
                          </div>
                          <div className="terminal-line">
                            <span className="terminal-output">Analysis complete. Accuracy: 99.4%</span>
                          </div>
                          <div className="terminal-line">
                            <span className="terminal-prompt">$</span>
                            <span className="terminal-command">_</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="featured-project-content">
              <div className="featured-badge">
                Featured Project
              </div>
              <h2 className="featured-project-title">AI-Powered Analytics Platform</h2>
              <p className="featured-project-description">
                A comprehensive analytics solution with machine learning capabilities to transform 
                raw data into actionable insights for businesses of all sizes.
              </p>
              <div className="featured-tech-stack">
                {['React', 'Node.js', 'TensorFlow', 'AWS', 'MongoDB'].map((tech, i) => (
                  <span key={i} className="featured-tech-badge">{tech}</span>
                ))}
              </div>
              <div className="featured-project-features">
                <div className="feature-item">
                  <div className="feature-icon">📊</div>
                  <div className="feature-text">Real-time data visualization</div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">🤖</div>
                  <div className="feature-text">ML-powered predictions</div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">🔒</div>
                  <div className="feature-text">Enterprise-grade security</div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">⚡</div>
                  <div className="feature-text">High-performance architecture</div>
                </div>
              </div>
              <div className="featured-project-cta">
                <button className="featured-cta-button project-view-button">
                  View Project
                </button>
                <button className="featured-cta-button project-source-button">
                  <span className="github-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
                    </svg>
                  </span>
                  Source Code
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>

      <motion.section 
        className="team-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <div className="section-header">
          <motion.h2
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Meet Our Experts
          </motion.h2>
          <motion.p
            className="section-subtext"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            The professional team behind our success
          </motion.p>
        </div>
        <TeamCarousel />
      </motion.section>

      <TestimonialSection />

      <BlogPostsSection />

      <motion.section 
        className="cta-section glass-morphism"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2>Ready to Transform Your Business?</h2>
        <p>Let's discuss how our technology solutions can help you achieve your goals</p>
        <motion.button 
          className="cta-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Contact Us Today
        </motion.button>
      </motion.section>
    </motion.div>
  );
};

export default HomePage;